import type { Shift } from '../types';

export const DEFAULT_UNPAID_MEAL_MINUTES = 30;

/** Short standard breaks through long chef / split-shift unpaid meals (15-minute steps). */
export const MIN_UNPAID_MEAL_MINUTES = 15;
export const MAX_UNPAID_MEAL_MINUTES = 240;

export function clampUnpaidMealMinutes(m: number): number {
  const step = 15;
  const rounded = Math.round(m / step) * step;
  return Math.min(MAX_UNPAID_MEAL_MINUTES, Math.max(MIN_UNPAID_MEAL_MINUTES, rounded));
}

export function isAllowedUnpaidMealMinutes(m: number | undefined): boolean {
  if (m == null || !Number.isFinite(m)) return false;
  if (m < MIN_UNPAID_MEAL_MINUTES || m > MAX_UNPAID_MEAL_MINUTES) return false;
  return m % 15 === 0;
}

export function parseHHMMToMinutes(s: string): number {
  const parts = s.split(':').map((x) => parseInt(x, 10));
  const h = Number.isFinite(parts[0]) ? parts[0]! : 0;
  const m = Number.isFinite(parts[1]) ? parts[1]! : 0;
  return h * 60 + m;
}

export function formatMinutesAsHHMM(total: number): string {
  const t = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(t / 60);
  const m = t % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Wall-clock span in minutes (same rule as roster scheduled span: cross-midnight adds 24h). */
export function shiftWallMinutes(start: string, end: string): number {
  const sm = parseHHMMToMinutes(start);
  const em = parseHHMMToMinutes(end);
  let diff = em - sm;
  if (diff < 0) diff += 24 * 60;
  return diff;
}

/** When end is not after start on the clock, the span crosses midnight — meal window logic is skipped in v1. */
export function isOvernightShift(start: string, end: string): boolean {
  const sm = parseHHMMToMinutes(start);
  const em = parseHHMMToMinutes(end);
  return em <= sm;
}

export function requiresUnpaidMeal(wallMinutes: number): boolean {
  return wallMinutes >= 4 * 60;
}

export function isQuarterHourTime(hhmm: string): boolean {
  const m = parseHHMMToMinutes(hhmm) % 60;
  return m % 15 === 0;
}

export function shiftMealValidationError(
  shift: Pick<Shift, 'start' | 'end' | 'mealBreakStart' | 'unpaidMealMinutes'>
): string | null {
  const wall = shiftWallMinutes(shift.start, shift.end);
  if (!requiresUnpaidMeal(wall)) return null;
  if (isOvernightShift(shift.start, shift.end)) return null;

  const mealLen = shift.unpaidMealMinutes ?? DEFAULT_UNPAID_MEAL_MINUTES;
  if (!isAllowedUnpaidMealMinutes(mealLen)) {
    return `Meal break length must be ${MIN_UNPAID_MEAL_MINUTES}–${MAX_UNPAID_MEAL_MINUTES} minutes, in 15-minute steps (e.g. 30, 120, 180).`;
  }
  if (!shift.mealBreakStart?.trim()) {
    return 'Unpaid meal break start is required for shifts of 4 hours or more (same day).';
  }
  if (!isQuarterHourTime(shift.mealBreakStart)) {
    return 'Meal break start must use 00, 15, 30, or 45 minutes.';
  }

  const sm = parseHHMMToMinutes(shift.start);
  const em = parseHHMMToMinutes(shift.end);
  const mm = parseHHMMToMinutes(shift.mealBreakStart);
  if (mm < sm) return 'Meal break must start on or after shift start.';
  if (mm + mealLen > em) return 'Meal break must end on or before shift end.';
  if (em - sm < mealLen) return 'Shift is too short for the selected meal break length.';
  return null;
}

/**
 * Paid scheduled minutes for roster / labor summaries.
 * Same-day 4h+ shifts: subtract unpaid meal when a valid mealBreakStart is set; if missing, subtract default meal length for legacy estimates.
 * Overnight spans: no meal deduction in v1.
 */
export function paidScheduledMinutes(
  shift: Pick<Shift, 'start' | 'end' | 'mealBreakStart' | 'unpaidMealMinutes'>
): number {
  const wall = shiftWallMinutes(shift.start, shift.end);
  if (isOvernightShift(shift.start, shift.end)) return wall;
  if (!requiresUnpaidMeal(wall)) return wall;

  const mealLen = shift.unpaidMealMinutes ?? DEFAULT_UNPAID_MEAL_MINUTES;
  if (shift.mealBreakStart?.trim()) {
    const err = shiftMealValidationError(shift);
    if (err) return wall;
    return Math.max(0, wall - mealLen);
  }
  return Math.max(0, wall - mealLen);
}

export function wallHoursRounded(start: string, end: string): number {
  return Math.round((shiftWallMinutes(start, end) / 60) * 10) / 10;
}

export function paidHoursRounded(shift: Pick<Shift, 'start' | 'end' | 'mealBreakStart' | 'unpaidMealMinutes'>): number {
  return Math.round((paidScheduledMinutes(shift) / 60) * 10) / 10;
}

/** Midpoint of same-day span, snapped to quarter-hour and clamped so the full meal fits inside the shift. */
export function suggestMealBreakStart(
  start: string,
  end: string,
  mealLen: number = DEFAULT_UNPAID_MEAL_MINUTES
): string {
  if (isOvernightShift(start, end)) return '12:00';
  const sm = parseHHMMToMinutes(start);
  const em = parseHHMMToMinutes(end);
  const len = clampUnpaidMealMinutes(mealLen);
  const maxStart = em - len;
  if (maxStart < sm) return formatMinutesAsHHMM(sm);
  let candidate = Math.round((sm + em) / 2 / 15) * 15;
  candidate = Math.max(sm, Math.min(maxStart, candidate));
  if (candidate + len > em) candidate = Math.max(sm, em - len);
  candidate = Math.floor(candidate / 15) * 15;
  candidate = Math.max(sm, Math.min(maxStart, candidate));
  return formatMinutesAsHHMM(candidate);
}

export function nzBreakComplianceHint(wallMinutes: number): string {
  const h = wallMinutes / 60;
  if (h < 2) return '';
  if (h < 4) return 'NZ minimum: 1× 10 min paid rest (within paid hours).';
  if (h < 6) return 'NZ minimum: 1× 10 min paid rest + 1× 30 min unpaid meal (schedule below).';
  if (h < 10) return 'NZ minimum: 2× 10 min paid rest + 1× 30 min unpaid meal (rests are paid; meal below).';
  return 'NZ minimum: 2× 10 min paid rest + 1× 30 min unpaid meal. Shifts over 10 hours may require a second unpaid meal—confirm with your advisor.';
}

export function nzTenPlusMealWarning(wallMinutes: number): boolean {
  return wallMinutes >= 10 * 60;
}

/** Human-readable same-day split: Work a–b · break · work c–d */
export function formatShiftWithMealSegments(
  start: string,
  end: string,
  mealBreakStart: string | undefined,
  unpaidMealMinutes: number = DEFAULT_UNPAID_MEAL_MINUTES
): string {
  if (!mealBreakStart?.trim() || isOvernightShift(start, end)) {
    return `${start}–${end}`;
  }
  const fake: Pick<Shift, 'start' | 'end' | 'mealBreakStart' | 'unpaidMealMinutes'> = {
    start,
    end,
    mealBreakStart,
    unpaidMealMinutes,
  };
  if (shiftMealValidationError(fake as Shift)) {
    return `${start}–${end}`;
  }
  const sm = parseHHMMToMinutes(start);
  const em = parseHHMMToMinutes(end);
  const mm = parseHHMMToMinutes(mealBreakStart);
  const afterBreak = mm + unpaidMealMinutes;
  return `Work ${start}–${mealBreakStart} · ${unpaidMealMinutes}m unpaid · ${formatMinutesAsHHMM(afterBreak)}–${end} (${wallHoursRounded(start, end)}h on site)`;
}

export function firstRosterMealValidationError(shifts: Shift[]): string | null {
  for (const s of shifts) {
    const err = shiftMealValidationError(s);
    if (err) return err;
  }
  return null;
}

/** Strip meal fields when wall time is under 4h or shift is overnight. */
export function normalizeShiftMealFields(s: Shift): Shift {
  const wall = shiftWallMinutes(s.start, s.end);
  if (isOvernightShift(s.start, s.end) || !requiresUnpaidMeal(wall)) {
    const { mealBreakStart: _m, unpaidMealMinutes: _u, ...rest } = s;
    return rest as Shift;
  }
  return s;
}

/** If a same-day 4h+ shift has no meal start yet, default to midpoint (quarter-hour). Review and stagger before relying on coverage. */
export function ensureMealStartsSuggested(shifts: Shift[]): Shift[] {
  return shifts.map((s) => {
    const base = normalizeShiftMealFields(s);
    const wall = shiftWallMinutes(base.start, base.end);
    if (isOvernightShift(base.start, base.end) || !requiresUnpaidMeal(wall)) return base;
    if (base.mealBreakStart?.trim()) return base;
    const mealMins = base.unpaidMealMinutes ?? DEFAULT_UNPAID_MEAL_MINUTES;
    return {
      ...base,
      mealBreakStart: suggestMealBreakStart(base.start, base.end, mealMins),
      unpaidMealMinutes: mealMins,
    };
  });
}
